import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Box, FormLabel, SxProps, Theme, styled } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    BRAND_LUXE_MUTED,
    BRAND_MODAL_ACCENT,
    BRAND_MODAL_ACCENT_ICON,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    brandModalOutlinedInputSx,
} from '../../pages/BrandDashboard/brandDashboardTokens';

dayjs.extend(utc);
dayjs.extend(timezone);

// Fix for TypeScript error: Property 'supportedValuesOf' does not exist on type 'typeof Intl'.
interface IntlConstructor {
    supportedValuesOf?(key: string): string[];
}
declare var Intl: IntlConstructor;

export interface ICustomDatePicker {
    label?: string;
    value?: string;
    onChange?: (e: any, timezone?: string) => void;
    minDate?: any;
    maxDate?: any;
    defaultValue?: any;
    ageLimit?: number;
    sx?: SxProps<Theme>;
    withTime?: boolean;
    withTimezone?: boolean;
    disabled?: boolean;
    timezone?: string;
    enableLabel?: boolean;
    /** Filled date field + adornment use pure black (matches calendar icon area). */
    uniformInputBlack?: boolean;
    /** Brand dashboard: #262626 surfaces, borderless + gold focus */
    brandLuxe?: boolean;
    /** When true, dates can only be chosen via the calendar popup (no keyboard entry). */
    calendarOnly?: boolean;
}

const StyledBox = styled(Box)(({ theme }) => ({
    width: '100%',
    border: '1px solid grey',
    borderRadius: '10px',
    overflow: 'hidden',
    color: 'white',
    backgroundColor: 'rgba(19, 21, 23, 1)',
    '&:focus-within': {
        border: `1px solid rgba(251, 188, 85, 1)`,
    },
}));

const saveTimeInTimezone = (
    date: Dayjs, // selectedDate from DatePicker
    time: Dayjs, // selectedTime from TimePicker
    timezone: string, // e.g. 'Africa/Lagos' or 'Etc/GMT-4'
): string | null => {
    if (!date?.isValid() || !time?.isValid() || !timezone) {
        return null;
    }

    // Extract the exact date/time components the user selected
    const year = date.year();
    const month = date.month();
    const day = date.date();
    const hour = time.hour();
    const minute = time.minute();
    const second = time.second();

    // Format as ISO string with the timezone offset
    // Create the datetime in the selected timezone with the exact values
    const datetimeStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(
        hour,
    ).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;

    // Parse in the timezone to get the offset, then format with offset
    const zoned = dayjs.tz(datetimeStr, timezone);
    if (!zoned.isValid()) {
        return null;
    }

    return zoned.format('YYYY-MM-DDTHH:mm:ssZ'); // This will include the correct offset like +04:00 or -04:00
};

const saveDateOnly = (date: Dayjs, useTimezone: boolean, timezone: string): string | null => {
    if (!date?.isValid()) {
        return null;
    }

    const normalized = useTimezone ? date.tz(timezone).startOf('day') : date.startOf('day');
    if (!normalized.isValid()) {
        return null;
    }

    return normalized.format('YYYY-MM-DD');
};

const parseDateOnlyValue = (val: string, timezone: string, useTimezone: boolean): Dayjs => {
    const valStr = String(val);

    if (/^\d{4}-\d{2}-\d{2}$/.test(valStr)) {
        const parsed = useTimezone ? dayjs.tz(valStr, timezone).startOf('day') : dayjs(valStr).startOf('day');
        return parsed.isValid() ? parsed : dayjs().startOf('day');
    }

    const parsed = useTimezone ? dayjs(valStr).tz(timezone).startOf('day') : dayjs(valStr).startOf('day');
    return parsed.isValid() ? parsed : dayjs().startOf('day');
};

const parseValuePreservingTime = (val: string | undefined, tz: string): Dayjs => {
    if (!val || !dayjs(val).isValid()) {
        return dayjs().tz(tz);
    }

    // Convert val to string to ensure we can use string methods
    const valStr = String(val);

    // Parse the value - handle different formats
    let parsedValue: Dayjs;
    if (valStr.endsWith('Z')) {
        // UTC format from backend (e.g., "2025-12-18T01:16:59.000Z")
        parsedValue = dayjs.utc(valStr);
    } else if (valStr.match(/[+-]\d{2}:\d{2}$/)) {
        // Has timezone offset (e.g., "2025-12-17T20:17:59+01:00")
        parsedValue = dayjs(valStr);
    } else {
        // No timezone info - assume UTC (common backend format)
        parsedValue = dayjs.utc(valStr);
    }

    // Convert to the selected timezone to get the wall clock time
    const inSelectedTz = parsedValue.tz(tz);

    // Extract the exact date/time components from the timezone-converted date
    // This preserves the "wall clock" time in the selected timezone
    const year = inSelectedTz.year();
    const month = inSelectedTz.month();
    const day = inSelectedTz.date();
    const hour = inSelectedTz.hour();
    const minute = inSelectedTz.minute();
    const second = inSelectedTz.second();

    // Create a datetime string with these exact components
    const datetimeStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(
        hour,
    ).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;

    // Parse this datetime string in the selected timezone
    // This preserves the exact wall clock time
    const parsed = dayjs.tz(datetimeStr, tz);
    return parsed.isValid() ? parsed : dayjs().tz(tz);
};

const toBoundDateInTimezone = (bound: unknown, timezone: string | undefined): Dayjs | undefined => {
    if (bound == null || bound === '') {
        return undefined;
    }

    let parsed: Dayjs;
    if (dayjs.isDayjs(bound)) {
        parsed = bound;
    } else if (bound instanceof Date || typeof bound === 'string' || typeof bound === 'number') {
        parsed = dayjs(bound);
    } else {
        return undefined;
    }

    if (!parsed.isValid()) {
        return undefined;
    }

    return timezone ? parsed.tz(timezone) : parsed.startOf('day');
};

export const CustomDatePicker: React.FC<ICustomDatePicker> = ({
    label,
    value,
    onChange,
    minDate,
    maxDate,
    defaultValue,
    sx,
    withTime,
    withTimezone = true,
    disabled,
    timezone,
    enableLabel,
    uniformInputBlack = false,
    brandLuxe = false,
    calendarOnly = false,
}) => {
    const { themeStyle } = useThemeContext();

    const timezoneOptions = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
    const [selectedTimezone, setSelectedTimezone] = React.useState(timezone ?? dayjs.tz.guess());
    const usePickerTimezone = withTimezone || Boolean(withTime);
    const pickerTimezone = usePickerTimezone ? selectedTimezone : undefined;

    const defaultTime = dayjs().tz(selectedTimezone);
    const defaultDate = dayjs().tz(selectedTimezone);

    const parseControlledValue = (val: string | undefined, tz: string): Dayjs | null => {
        if (!val || !dayjs(val).isValid()) {
            return withTime ? dayjs().tz(tz) : null;
        }

        if (!withTime) {
            return parseDateOnlyValue(val, tz, usePickerTimezone);
        }

        return parseValuePreservingTime(val, tz);
    };

    const [selectedTime, setSelectedTime] = React.useState<Dayjs>(() => {
        const parsed = value ? parseControlledValue(value, selectedTimezone) : null;
        return parsed && withTime ? parsed : defaultTime;
    });
    const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(() => {
        if (value) {
            return parseControlledValue(value, selectedTimezone);
        }
        return withTime ? defaultDate : null;
    });
    const isInternalUpdate = React.useRef(false);

    // Update timezone when prop changes externally
    React.useEffect(() => {
        if (timezone && timezone !== selectedTimezone) {
            setSelectedTimezone(timezone);
        }
    }, [timezone, selectedTimezone]);

    // Update date/time when value changes (but not when we're updating internally)
    React.useEffect(() => {
        if (isInternalUpdate.current) {
            // Reset the flag and skip update if this is an internal update
            isInternalUpdate.current = false;
            return;
        }

        if (value && dayjs(value).isValid()) {
            const localTime = parseControlledValue(value, selectedTimezone);
            if (localTime) {
                setSelectedTime(localTime);
                setSelectedDate(localTime);
            }
        } else if (!withTime) {
            setSelectedDate(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, selectedTimezone, withTime, usePickerTimezone]);

    const handleDateChange = (newDate: Dayjs | null) => {
        if (!newDate?.isValid()) {
            return;
        }

        const dateInTimezone = usePickerTimezone ? newDate.tz(selectedTimezone) : newDate.startOf('day');
        setSelectedDate(dateInTimezone);
        isInternalUpdate.current = true; // Mark as internal update

        if (!withTime) {
            const final = saveDateOnly(dateInTimezone, usePickerTimezone, selectedTimezone);
            if (final) {
                onChange?.(final, usePickerTimezone ? selectedTimezone : undefined);
            }
            return;
        }

        const timeToUse = selectedTime.isValid() ? selectedTime : defaultTime;
        if (!selectedTime.isValid()) {
            setSelectedTime(timeToUse);
        }

        const final = saveTimeInTimezone(dateInTimezone, timeToUse, selectedTimezone);
        if (final) {
            onChange?.(final, selectedTimezone);
        }
    };

    const handleTimeChange = (newTime: Dayjs | null) => {
        if (!newTime?.isValid()) {
            return;
        }

        // Ensure the time is in the correct timezone
        const timeInTimezone = newTime.tz(selectedTimezone);

        // If no date is selected yet, use today's date
        // Otherwise, preserve the existing date
        const dateToUse = selectedDate?.isValid() ? selectedDate : defaultDate;
        setSelectedTime(timeInTimezone);
        if (!selectedDate?.isValid()) {
            setSelectedDate(dateToUse);
        }
        isInternalUpdate.current = true; // Mark as internal update

        // Use the date from dateToUse (preserved) and time from newTime
        const final = saveTimeInTimezone(dateToUse, timeInTimezone, selectedTimezone);
        if (final) {
            onChange?.(final, selectedTimezone);
        }
    };

    const brandPickerLayoutSx = {
        backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
        color: '#f2f2f6',
        '& .MuiDayCalendar-weekDayLabel': {
            color: BRAND_LUXE_MUTED,
        },
    };

    const calendarOnlyInputProps = calendarOnly
        ? {
              readOnly: true,
              onKeyDown: (e: React.KeyboardEvent) => e.preventDefault(),
          }
        : undefined;

    const dateTextFieldSlotProps = brandLuxe
        ? {
              variant: 'outlined' as const,
              fullWidth: true,
              inputProps: calendarOnlyInputProps,
              sx: {
                  ...brandModalOutlinedInputSx,
                  '& .MuiOutlinedInput-input': {
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      paddingTop: '13px',
                      paddingBottom: '13px',
                      color: '#f2f2f6',
                  },
              } as SxProps<Theme>,
          }
        : uniformInputBlack
        ? {
              variant: 'filled' as const,
              fullWidth: true,
              sx: {
                  width: '100%',
                  '& .MuiFilledInput-root': {
                      width: '100%',
                      backgroundColor: '#000000',
                      '&:before': { display: 'none' },
                      '&:after': { display: 'none' },
                      '&:hover': { backgroundColor: '#000000' },
                      '&.Mui-focused': { backgroundColor: '#000000' },
                  },
                  '& .MuiInputBase-input': {
                      backgroundColor: '#000000',
                  },
              },
              inputProps: {
                  ...calendarOnlyInputProps,
                  style: {
                      color: '#ffffff',
                      backgroundColor: '#000000',
                      paddingTop: 0,
                      paddingBottom: 0,
                      height: '50px',
                  },
              },
              InputProps: {
                  disableUnderline: true,
                  sx: {
                      backgroundColor: '#000000',
                      '& .MuiInputAdornment-root': {
                          backgroundColor: '#000000',
                      },
                  },
              },
          }
        : {
              variant: 'filled' as const,
              fullWidth: true,
              sx: { width: '100%' },
              inputProps: {
                  ...calendarOnlyInputProps,
                  style: {
                      color: theme[themeStyle].lightWhite,
                      backgroundColor: theme[themeStyle].gray_text,
                      paddingTop: 0,
                      paddingBottom: 0,
                      height: '50px',
                  },
              },
              InputProps: {
                  disableUnderline: true,
              },
          };

    const pickersLayoutSx: SxProps<Theme> = withTime
        ? {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'stretch',
              width: '100%',
              gap: 1,
              '& .MuiFormControl-root': {
                  flex: 1,
                  minWidth: 0,
                  width: '100%',
              },
              '& .MuiPickersInputBase-root': {
                  width: '100%',
              },
              '& .MuiFilledInput-root': {
                  width: '100%',
              },
          }
        : {
              width: '100%',
              '& .MuiFormControl-root': { width: '100%' },
          };

    const pickerFieldSx: SxProps<Theme> = {
        flex: withTime ? 1 : undefined,
        width: '100%',
        minWidth: 0,
    };

    return (
        <>
            {enableLabel && (
                <FormLabel
                    style={{
                        width: '100%',
                        textAlign: 'left',
                        fontSize: 14,
                        fontStyle: 'normal',
                        fontWeight: '400',
                        color: theme[themeStyle].form_label,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    }}>
                    {label}
                </FormLabel>
            )}
            <StyledBox
                sx={{
                    ...(brandLuxe
                        ? {
                              border: 'none',
                              backgroundColor: 'transparent',
                              overflow: 'visible',
                              '&:focus-within': {
                                  border: 'none',
                                  boxShadow: 'none',
                              },
                          }
                        : {
                              '&:focus-within': {
                                  border: '1px solid grey',
                              },
                              backgroundColor: theme[themeStyle].tag_white_light,
                          }),
                    ...sx,
                }}>
                {withTimezone && (
                    <Box sx={{ mb: 1, pr: 1 }}>
                        <select
                            value={selectedTimezone}
                            onChange={e => {
                                const newTz = e.target.value;
                                setSelectedTimezone(newTz);

                                if (selectedDate?.isValid() && selectedTime?.isValid()) {
                                    isInternalUpdate.current = true; // Mark as internal update
                                    const final = saveTimeInTimezone(selectedDate, selectedTime, newTz);
                                    if (final && onChange) {
                                        onChange(final, newTz);
                                    }
                                }
                            }}
                            style={{
                                width: '100%',
                                height: '40px',
                                backgroundColor: brandLuxe
                                    ? BRAND_SURFACE_CONTAINER_HIGHEST
                                    : theme[themeStyle].gray_text,
                                color: brandLuxe ? '#f2f2f6' : theme[themeStyle].lightWhite,
                                border: brandLuxe ? `1px solid ${BRAND_OUTLINE_VARIANT}` : 'none',
                                borderRadius: brandLuxe ? '12px' : '6px',
                                padding: '0 8px',
                                outline: 'none',
                            }}>
                            {timezoneOptions.map((tz: any) => (
                                <option key={tz} value={tz}>
                                    {tz}
                                </option>
                            ))}
                        </select>
                    </Box>
                )}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={pickersLayoutSx}>
                        <DatePicker
                            value={selectedDate}
                            minDate={toBoundDateInTimezone(minDate, pickerTimezone)}
                            maxDate={toBoundDateInTimezone(maxDate, pickerTimezone)}
                            disabled={disabled}
                            timezone={pickerTimezone}
                            onChange={handleDateChange}
                            sx={pickerFieldSx}
                            slotProps={{
                                textField: dateTextFieldSlotProps,
                                layout: {
                                    sx: brandLuxe
                                        ? brandPickerLayoutSx
                                        : {
                                              backgroundColor: theme[themeStyle].gray_text,
                                              color: theme[themeStyle].lightWhite,
                                              '& .MuiDayCalendar-weekDayLabel': {
                                                  color: `${theme[themeStyle].light_gray_text}`,
                                              },
                                          },
                                },
                                nextIconButton: {
                                    sx: {
                                        color: brandLuxe ? BRAND_MODAL_ACCENT : theme[themeStyle].primary_orange,
                                        ':disabled': {
                                            color: brandLuxe
                                                ? `${BRAND_LUXE_MUTED} !important`
                                                : `${theme[themeStyle].light_gray_text} !important`,
                                        },
                                    },
                                },
                                previousIconButton: {
                                    sx: {
                                        color: brandLuxe ? BRAND_MODAL_ACCENT : theme[themeStyle].primary_orange,
                                        ':disabled': {
                                            color: brandLuxe
                                                ? `${BRAND_LUXE_MUTED} !important`
                                                : `${theme[themeStyle].light_gray_text} !important`,
                                        },
                                    },
                                },
                                day: {
                                    sx: {
                                        color: brandLuxe ? '#f2f2f6' : theme[themeStyle].lightWhite,
                                        ':disabled': {
                                            color: brandLuxe
                                                ? `${BRAND_LUXE_MUTED} !important`
                                                : `${theme[themeStyle].light_gray_text} !important`,
                                        },
                                        headerColor: 'white !important',
                                    },
                                },
                                switchViewButton: {
                                    sx: {
                                        color: brandLuxe ? BRAND_MODAL_ACCENT : theme[themeStyle].primary_orange,
                                    },
                                },
                                openPickerButton: {
                                    sx: {
                                        color: brandLuxe ? BRAND_MODAL_ACCENT_ICON : theme[themeStyle].lightWhite,
                                    },
                                },
                            }}
                        />
                        {withTime && (
                            <MuiTimePicker
                                value={selectedTime}
                                disabled={disabled}
                                // minTime={minDate ? dayjs(minDate).tz(selectedTimezone) : undefined}
                                timeSteps={{
                                    hours: 1,
                                    minutes: 1,
                                    seconds: 1,
                                }}
                                sx={pickerFieldSx}
                                timezone={selectedTimezone}
                                onChange={handleTimeChange}
                                slotProps={{
                                    textField: dateTextFieldSlotProps,
                                    layout: {
                                        sx: brandLuxe
                                            ? brandPickerLayoutSx
                                            : {
                                                  backgroundColor: theme[themeStyle].gray_text,
                                                  color: theme[themeStyle].lightWhite,
                                                  '& .MuiDayCalendar-weekDayLabel': {
                                                      color: `${theme[themeStyle].light_gray_text}`,
                                                  },
                                              },
                                    },
                                    openPickerButton: {
                                        sx: {
                                            color: brandLuxe ? BRAND_MODAL_ACCENT_ICON : theme[themeStyle].lightWhite,
                                        },
                                    },
                                }}
                            />
                        )}
                    </Box>
                </LocalizationProvider>
            </StyledBox>
        </>
    );
};
